import React from 'react';
import { Link } from 'react-router-dom';
import LoadError from '@/components/common/LoadError';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import BudgetLine from './BudgetLine';

/**
 * A budget read with "none set" as null: the server answers 404 when the
 * program has no budget for the term, which is not a failed read. Any other
 * failure still rejects.
 */
export const budgetOrNone = (read) =>
    Promise.resolve(read).catch((err) => {
        if (err?.response?.status === 404) return null;
        throw err;
    });

/**
 * The Overview's budget line in each of its states: the line itself, a
 * neutral "No budget set" when the program has none for the term (only an
 * admin can set one, so only an admin is offered the way), or an error with
 * Retry when the read failed. Nothing while the term's data is loading.
 */
function BudgetStatus({
    budget,
    failed,
    loading,
    onRetry,
    termName,
    unit,
    unitPlural,
}) {
    const { userProfile, effectiveRoles } = useAuth();
    const isAdmin = resolveRoles({ effectiveRoles, userProfile }).includes(
        'admin'
    );
    if (failed) {
        return <LoadError what="the budget for this term" onRetry={onRetry} />;
    }
    if (budget) {
        return (
            <BudgetLine budget={budget} unit={unit} unitPlural={unitPlural} />
        );
    }
    if (loading) return null;
    return (
        // A fact, not an alert: nothing went wrong
        <p className="border rounded bg-light py-2 px-3 mb-3 coordinator-budget-line">
            <strong>No budget set{termName ? ` for ${termName}` : ''}.</strong>{' '}
            {isAdmin ? (
                <Link to="/admin?tab=budgets">Set the budget</Link>
            ) : (
                <span className="text-body-secondary">
                    An administrator sets each program's budget.
                </span>
            )}
        </p>
    );
}

export default BudgetStatus;
